import * as React from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

const Drawer = ({
  shouldScaleBackground = true,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    {...props}
  />
)
Drawer.displayName = 'Drawer'

const DrawerTrigger = DrawerPrimitive.Trigger

const DrawerPortal = DrawerPrimitive.Portal

const DrawerClose = DrawerPrimitive.Close

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('~fixed ~inset-0 ~z-50 ~bg-black/80', className)}
    {...props}
  />
))
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    drawerHandleClassName?: string
    closeIconVisible?: boolean
    closeClassName?: string
    overlayClassName?: string
    /**
     * Renders a fixed layer between overlay and sheet (e.g. white) so transparent
     * pixels outside `border-radius` do not show the dimmed scrim in the top corners.
     */
    topFillClassName?: string
  }
>(
  (
    {
      className,
      children,
      drawerHandleClassName,
      closeIconVisible,
      closeClassName,
      overlayClassName,
      topFillClassName,
      ...props
    },
    ref
  ) => (
    <DrawerPortal>
      <DrawerOverlay className={overlayClassName} />
      {topFillClassName ? (
        <div
          className={cn(
            '~pointer-events-none ~fixed ~inset-x-0 ~z-[1095]',
            topFillClassName
          )}
          aria-hidden
        />
      ) : null}
      <DrawerPrimitive.Content
        ref={ref}
        className={cn(
          '~fixed ~inset-x-0 ~bottom-0 ~z-[1100] ~mt-24 ~flex ~h-auto ~flex-col ~rounded-t-lg ~border ~bg-background',
          className
        )}
        {...props}
      >
        <div
          className={cn(
            '~mx-auto ~mt-4 ~h-2 ~w-[100px] ~rounded-full ~bg-muted',
            drawerHandleClassName
          )}
        />
        {children}
        {closeIconVisible && (
          <DrawerPrimitive.Close
            className={cn(
              '~absolute ~right-4 ~top-4 ~rounded-3xs ~opacity-70 ~ring-offset-background ~transition-opacity hover:~opacity-100 focus:~outline-none focus:~ring-2 focus:~ring-ring focus:~ring-offset-2 disabled:~pointer-events-none ~text-charcoal ~bg-transparent data-[state=open]:~bg-accent data-[state=open]:~text-muted-foreground',
              closeClassName
            )}
          >
            <X className="~h-7 ~w-7" />
            <span className="~sr-only">Close</span>
          </DrawerPrimitive.Close>
        )}
      </DrawerPrimitive.Content>
    </DrawerPortal>
  )
)
DrawerContent.displayName = 'DrawerContent'

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('~grid ~gap-1.5 ~p-4 ~text-left', className)} {...props} />
)
DrawerHeader.displayName = 'DrawerHeader'

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('~mt-auto ~flex ~flex-col ~gap-2 ~p-4', className)}
    {...props}
  />
)
DrawerFooter.displayName = 'DrawerFooter'

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      '~text-[32px] ~font-semibold ~leading-none ~tracking-tight',
      className
    )}
    {...props}
  />
))
DrawerTitle.displayName = DrawerPrimitive.Title.displayName

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('~text-sm ~text-muted-foreground', className)}
    {...props}
  />
))
DrawerDescription.displayName = DrawerPrimitive.Description.displayName

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
}
